import {
  ResetIcon,
  TrackNextIcon,
  TrackPreviousIcon,
} from '@radix-ui/react-icons';
import {
  Box,
  Card,
  ChevronDownIcon,
  DataList,
  Flex,
  Grid,
  Heading,
  Progress,
  Skeleton,
  Spinner,
  Text,
} from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonMenuBar } from 'components/common/menu-bar';
import { MachineCameraStreamViewer } from 'components/machine/camera-stream-viewer';
import { MachineContext } from 'contexts/machine.context';
import { CHART_COLORS } from 'enums/charts.enums';
import { IMenuBarMenu } from 'interfaces/i-menu-bars';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IVisionCalibrationStatus } from 'lib_ts/interfaces/machine-msg/vision/i-calibrate-camera';
import {
  ICameraError,
  IVisionCameraFrame,
} from 'lib_ts/interfaces/vision/i-vision-camera-calibration';
import { clamp, first } from 'lodash';
import { useContext, useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';
import {
  NameType,
  ValueType,
} from 'recharts/types/component/DefaultTooltipContent';

interface IHistogramData {
  index: number;
  error: number;
}

const CustomTooltip = ({
  active,
  payload,
}: TooltipProps<ValueType, NameType>) => {
  if (!active) {
    return null;
  }

  if (!payload) {
    return null;
  }

  if (payload.length === 0) {
    return null;
  }

  const data: IHistogramData = first(payload)?.payload;

  if (!data) {
    return null;
  }

  return (
    <div className="custom-tooltip">
      Image Index: {data.index}
      <br />
      Error: {data.error.toFixed(2)}
    </div>
  );
};

export const VisionCalibrationChart = (props: {
  header: string;
  error: ICameraError;
}) => {
  const data = useMemo(() => {
    return props.error.reprojection.map((v, i) => {
      const o: IHistogramData = {
        index: i,
        error: v,
      };

      return o;
    });
  }, [props.error]);

  const imgIndicesOver = useMemo(() => {
    const output: number[] = [];

    props.error.reprojection.forEach((v, i) => {
      if (v <= props.error.cutoff) {
        return;
      }

      output.push(i);
    });

    return output;
  }, [props.error.reprojection, props.error.cutoff]);

  const maxYAxis = useMemo(
    () => Math.ceil(100 * Math.max(...props.error.reprojection)) / 100,
    [props.error.reprojection]
  );

  const imgIndicesOverText = imgIndicesOver.map((v) => `Img ${v}`).join(', ');

  return (
    <ErrorBoundary identifier="VCCResultTab">
      <Card style={{ width: '100%' }}>
        <Heading size="3" mb="4">
          {props.header}
        </Heading>

        <Grid columns="6" gap="4" style={{ width: '100%' }}>
          <Box gridColumn="span 5">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart
                data={data}
                margin={{
                  top: 20,
                  bottom: 20,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />

                <XAxis
                  name="Image Index"
                  type="category"
                  dataKey="index"
                  interval={4}
                />

                <YAxis
                  name="Reprojection Error"
                  type="number"
                  dataKey="error"
                  orientation="left"
                  domain={[0, maxYAxis]}
                  interval="preserveStartEnd"
                />

                <Tooltip content={<CustomTooltip />} />

                <Bar
                  name="Reprojection Error"
                  dataKey="error"
                  fill={CHART_COLORS[0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>

          <DataList.Root size="1" orientation="vertical">
            <DataList.Item>
              <DataList.Label>
                <Text truncate>Cutoff</Text>
              </DataList.Label>
              <DataList.Value>{props.error.cutoff}</DataList.Value>
            </DataList.Item>
            <DataList.Item>
              <DataList.Label>
                <Text truncate>Count &gt; {props.error.cutoff}</Text>
              </DataList.Label>
              <DataList.Value>
                <Text truncate>{imgIndicesOver.length}</Text>
              </DataList.Value>
            </DataList.Item>
            <DataList.Item>
              <DataList.Label>
                <Text truncate>Images &gt; {props.error.cutoff}</Text>
              </DataList.Label>
              <DataList.Value>
                <Text title={imgIndicesOverText || undefined} truncate>
                  {imgIndicesOverText || 'N/A'}
                </Text>
              </DataList.Value>
            </DataList.Item>
          </DataList.Root>
        </Grid>
      </Card>
    </ErrorBoundary>
  );
};

export const VisionCalibrationDataCollectionTab = (props: {
  status: IVisionCalibrationStatus;
  capturedFrames: IVisionCameraFrame[];
  workingIdx: number;
  frameIndicesToRetake: number[];
  loading: boolean;
  setWorkingIdx: (idx: number) => void;
  onReset: (date: number) => void;
}) => {
  const { machine } = useContext(MachineContext);
  const {
    status,
    workingIdx,
    capturedFrames,
    frameIndicesToRetake,
    loading,
    setWorkingIdx,
    onReset,
  } = props;

  const isRetaking = frameIndicesToRetake.length > 0;

  const menus = useMemo(() => {
    const framesBefore = capturedFrames
      .filter((f) => f.index < workingIdx)
      .sort((a, b) => (a.index < b.index ? 1 : -1));

    const framesAfter = capturedFrames
      .filter((f) => f.index > workingIdx)
      .sort((a, b) => (a.index < b.index ? -1 : 1));

    const output: IMenuBarMenu[] = [
      {
        id: 'prev-frames',
        trigger: <TrackPreviousIcon />,
        disabled: framesBefore.length === 0,
        items: framesBefore.map((f) => ({
          id: `prev-frame-${f.index}`,
          label: `Frame ${f.index + 1}`,
          onClick: () => setWorkingIdx(f.index),
          disabled: isRetaking && !frameIndicesToRetake.includes(f.index),
          color:
            isRetaking && !frameIndicesToRetake.includes(f.index)
              ? RADIX.COLOR.SECONDARY
              : undefined,
        })),
      },
      {
        id: 'next-frames',
        trigger: <TrackNextIcon />,
        disabled: framesAfter.length === 0,
        items: framesAfter.map((f) => ({
          id: `next-frame-${f.index}`,
          label: `Frame ${f.index + 1}`,
          onClick: () => setWorkingIdx(f.index),
          disabled: isRetaking && !frameIndicesToRetake.includes(f.index),
          color:
            isRetaking && !frameIndicesToRetake.includes(f.index)
              ? RADIX.COLOR.SECONDARY
              : undefined,
        })),
      },
      {
        id: 'actions',
        trigger: <ChevronDownIcon />,
        items: [
          {
            id: 'start-over',
            label: 'Start Over',
            startSlot: <ResetIcon />,
            onClick: () => onReset(Date.now()),
          },
        ],
      },
    ];

    return output;
  }, [status.capturing, capturedFrames, workingIdx, frameIndicesToRetake]);

  return (
    <ErrorBoundary identifier="VCCDataCollectionTabTab">
      <Flex direction="column" gap="4">
        <MachineCameraStreamViewer
          machineID={machine.machineID}
          hideSource
          hideRefreshRate
        />

        <Card>
          <Flex
            p="1" // +3 (from the card) = 4
            gap="4"
            align="center"
            style={{ width: '100%' }}
          >
            <Flex direction="column" gap="2" flexGrow="1">
              <Skeleton loading={loading}>
                <Flex justify="between" gap="2">
                  <Flex gap="2" align="center">
                    {status.capturing && <Spinner />}
                    <Text>{status.label}</Text>
                  </Flex>

                  <Text color={RADIX.COLOR.SECONDARY}>
                    {isRetaking ? 'Retaking ' : ''}
                    Image {`${workingIdx + 1}/${status.totalFrames}`}
                  </Text>
                </Flex>
              </Skeleton>
              <Skeleton loading={loading}>
                <Progress
                  value={
                    status.totalFrames
                      ? 100 *
                        clamp((status.maxIdx + 1) / status.totalFrames, 0, 1)
                      : undefined
                  }
                />
              </Skeleton>
            </Flex>
            <Skeleton loading={loading}>
              <Box>
                <CommonMenuBar menus={menus} />
              </Box>
            </Skeleton>
          </Flex>
        </Card>
      </Flex>
    </ErrorBoundary>
  );
};
